import React, { useEffect, useState } from "react";
import { ymd } from "../lib/dates";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Printer, TagSimple } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatIDR, formatDate, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { generatePDF } from "../lib/pdf";
import { downloadExpenseReceipt } from "../lib/receiptPdf";

export default function Pengeluaran() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [lembagas, setLembagas] = useState([]);
  const [NOMEN, setNOMEN] = useState([]);
  const [CATEGORIES, setCATEGORIES] = useState([]);
  const [settings, setSettings] = useState({});
  const [catOpen, setCatOpen] = useState(false);
  const [newCat, setNewCat] = useState("");
  const today = ymd();
  const monthStart = today.slice(0, 8) + "01";
  const [start, setStart] = useState(monthStart);
  const [end, setEnd] = useState(today);
  const [filterNomen, setFilterNomen] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const canManage = ["superadmin","admin","finance"].includes(user?.role);

  const load = async () => {
    const q = `?start_date=${start}&end_date=${end}` + (filterNomen !== "all" ? `&nomenklatur_code=${filterNomen}` : "");
    const [p, l, n, c, sett] = await Promise.all([
      api.get(`/pengeluaran${q}`), api.get("/lembaga"), api.get("/nomenklatur"), api.get("/expense-categories"),
      api.get("/settings").catch(() => ({ data: {} })),
    ]);
    setItems(p.data); setLembagas(l.data); setNOMEN(n.data); setCATEGORIES(c.data.map((x) => x.name));
    setSettings(sett.data);
  };
  useEffect(() => { load(); }, [start, end, filterNomen]);

  const printExpense = async (e) => {
    try {
      const lembaga = lembagas.find((l) => l.id === e.lembaga_id);
      const nomenklatur = NOMEN.find((n) => n.code === e.nomenklatur_code);
      await downloadExpenseReceipt({ expense: e, lembaga, nomenklatur, settings });
      toast.success("Bukti pengeluaran diunduh");
    } catch (err) { toast.error("Gagal cetak: " + (err.message || "")); }
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try { await api.post("/expense-categories", { name: newCat.trim() }); toast.success("Kategori ditambahkan"); setNewCat(""); setCatOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const lMap = Object.fromEntries(lembagas.map((l) => [l.id, l]));

  const openNew = () => {
    setEditing(null);
    setForm({ lembaga_id: "", nomenklatur_code: "AJP", category: "Sarpras", description: "", amount: 0, expense_date: today, vendor: "", receipt_no: "" });
    setOpen(true);
  };
  const openEdit = (it) => { setEditing(it); setForm({ ...it, lembaga_id: it.lembaga_id || "" }); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...form, amount: Number(form.amount || 0), lembaga_id: form.lembaga_id || null };
      if (editing) await api.put(`/pengeluaran/${editing.id}`, payload);
      else await api.post("/pengeluaran", payload);
      toast.success("Pengeluaran tersimpan");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!confirm("Hapus pengeluaran?")) return;
    try { await api.delete(`/pengeluaran/${id}`); toast.success("Terhapus"); load(); } catch { toast.error("Gagal"); }
  };

  const total = items.reduce((a, b) => a + (b.amount || 0), 0);
  const perNomen = NOMEN.map((n) => ({ ...n, total: items.filter((i) => i.nomenklatur_code === n.code).reduce((a, b) => a + (b.amount || 0), 0) }));

  const printPDF = () => {
    generatePDF({
      title: "Laporan Pengeluaran",
      subtitle: `${formatDate(start)} — ${formatDate(end)}`,
      headers: ["Tanggal", "Nomenklatur", "Kategori", "Deskripsi", "Vendor", "Jumlah"],
      rows: items.map((e) => [formatDate(e.expense_date), e.nomenklatur_code, e.category, e.description, e.vendor || "-", formatIDR(e.amount)]),
      meta: { "Total Pengeluaran": formatIDR(total), "Jumlah": items.length },
      filename: `pengeluaran-${Date.now()}.pdf`,
    });
  };

  return (
    <div data-testid="pengeluaran-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Keuangan</div>
          <h1 className="font-display text-3xl font-black text-gray-900">Pengeluaran Nomenklatur</h1>
          <p className="text-gray-600 text-sm mt-1">Kelola pengeluaran internal per nomenklatur.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={printPDF} data-testid="pengeluaran-print-button"><Printer size={16} className="mr-1.5" />Cetak PDF</Button>
          {canManage && <Button variant="outline" onClick={()=>setCatOpen(true)} data-testid="pengeluaran-add-cat-button"><TagSimple size={16} className="mr-1.5" />Kelola Kategori</Button>}
          {canManage && <Button onClick={openNew} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="pengeluaran-add-button"><Plus size={16} className="mr-1.5" />Tambah Pengeluaran</Button>}
        </div>
      </div>

      {/* Nomenklatur summary */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        {perNomen.map((n) => (
          <div key={n.code} className="bg-white border border-gray-200 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">{n.code}</div>
            <div className="font-mono text-sm font-bold text-[#DC2626] mt-1">{formatIDR(n.total)}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row gap-3">
          <div className="flex gap-2 items-end">
            <div><Label className="text-xs">Dari</Label><Input type="date" value={start} onChange={(e)=>setStart(e.target.value)} /></div>
            <div><Label className="text-xs">Sampai</Label><Input type="date" value={end} onChange={(e)=>setEnd(e.target.value)} /></div>
          </div>
          <Select value={filterNomen} onValueChange={setFilterNomen}>
            <SelectTrigger className="w-40 bg-white sm:ml-auto self-end"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Semua Nomenklatur</SelectItem>
              {NOMEN.map((n)=><SelectItem key={n.code} value={n.code}>{n.code}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Tanggal</th>
                <th className="px-4 py-3 font-semibold">Nomenklatur</th>
                <th className="px-4 py-3 font-semibold">Kategori</th>
                <th className="px-4 py-3 font-semibold">Deskripsi</th>
                <th className="px-4 py-3 font-semibold">Lembaga</th>
                <th className="px-4 py-3 font-semibold">Vendor</th>
                <th className="px-4 py-3 font-semibold text-right">Jumlah</th>
                <th className="px-4 py-3 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-mono text-xs">{formatDate(e.expense_date)}</td>
                  <td className="px-4 py-2.5"><span className="inline-block px-2 py-0.5 rounded bg-red-50 text-red-700 text-xs font-mono font-semibold">{e.nomenklatur_code}</span></td>
                  <td className="px-4 py-2.5 text-xs">{e.category}</td>
                  <td className="px-4 py-2.5 max-w-xs truncate">{e.description}</td>
                  <td className="px-4 py-2.5 text-xs">{lMap[e.lembaga_id]?.name || "Yayasan"}</td>
                  <td className="px-4 py-2.5 text-xs">{e.vendor || "-"}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold text-[#DC2626]">{formatIDR(e.amount)}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button onClick={()=>printExpense(e)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600" title="Cetak Bukti" data-testid={`pengeluaran-print-${e.id}`}><Printer size={14} weight="duotone" /></button>
                    {canManage && <>
                      <button onClick={()=>openEdit(e)} className="p-1.5 hover:bg-gray-100 rounded" data-testid={`pengeluaran-edit-${e.id}`}><PencilSimple size={14} /></button>
                      <button onClick={()=>remove(e.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`pengeluaran-delete-${e.id}`}><Trash size={14} /></button>
                    </>}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-gray-500">Belum ada pengeluaran</td></tr>}
            </tbody>
            {items.length > 0 && (
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan={6} className="px-4 py-3 font-semibold">Total</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-[#DC2626]">{formatIDR(total)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="pengeluaran-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Pengeluaran" : "Tambah Pengeluaran"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nomenklatur *</Label>
                <Select value={form.nomenklatur_code} onValueChange={(v)=>setForm({...form, nomenklatur_code: v})}>
                  <SelectTrigger className="bg-white" data-testid="pengeluaran-nomen-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{NOMEN.map((n)=><SelectItem key={n.code} value={n.code}>{n.code} - {n.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Kategori *</Label>
                <Select value={form.category} onValueChange={(v)=>setForm({...form, category: v})}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{CATEGORIES.map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Deskripsi *</Label><Textarea value={form.description || ""} onChange={(e)=>setForm({...form, description: e.target.value})} data-testid="pengeluaran-description-input" /></div>
              <div><Label>Jumlah *</Label><Input type="number" value={form.amount || 0} onChange={(e)=>setForm({...form, amount: e.target.value})} data-testid="pengeluaran-amount-input" /></div>
              <div><Label>Tanggal *</Label><Input type="date" value={form.expense_date} onChange={(e)=>setForm({...form, expense_date: e.target.value})} /></div>
              <div><Label>Lembaga (opsional)</Label>
                <Select value={form.lembaga_id || "none"} onValueChange={(v)=>setForm({...form, lembaga_id: v === "none" ? "" : v})}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="none">Yayasan (Umum)</SelectItem>{lembagas.map((l)=><SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Vendor / Toko</Label><Input value={form.vendor || ""} onChange={(e)=>setForm({...form, vendor: e.target.value})} /></div>
              <div className="col-span-2"><Label>No. Nota</Label><Input value={form.receipt_no || ""} onChange={(e)=>setForm({...form, receipt_no: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="pengeluaran-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="bg-white" data-testid="pengeluaran-cat-dialog">
          <DialogHeader><DialogTitle>Kelola Kategori Pengeluaran</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md">
              {CATEGORIES.map((c) => (
                <div key={c} className="px-3 py-2 border-b border-gray-100 flex items-center justify-between text-sm">
                  <span>{c}</span>
                  <button onClick={async ()=>{
                    if (!confirm(`Hapus kategori "${c}"?`)) return;
                    const cats = await api.get("/expense-categories");
                    const target = cats.data.find((x)=>x.name === c);
                    if (target) { await api.delete(`/expense-categories/${target.id}`); toast.success("Terhapus"); load(); }
                  }} className="text-red-600 hover:bg-red-50 p-1 rounded"><Trash size={13} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={newCat} onChange={(e)=>setNewCat(e.target.value)} placeholder="Nama kategori baru" data-testid="pengeluaran-new-cat-input" />
              <Button onClick={addCategory} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="pengeluaran-save-cat-button"><Plus size={16} /></Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCatOpen(false)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
